// #region Imports
import React, { useState, useEffect } from 'react';
import './FuncionariosTable.scss';
import iconEdit from '../../../assets/edit.svg';
import iconTrash from '../../../assets/trash.svg';
import { formatCPF, formatPhone, formatDateISO } from '../../../../shared/helpers';
// #endregion

export default function FuncionariosTable({
  loading = false,
  filters,
  cargos = [],
  data = [],
  total = 0,
  page = 1,
  pageSize = 10,
  onFiltersChange = () => {},
  onPageChange = () => {},
  onCreate = () => {},
  onEdit = () => {},
  onDelete = () => {},
}) {
  // #region Estado filtros locais
  const [local, setLocal] = useState(filters);
  useEffect(() => { setLocal(filters); }, [filters]);
  // #endregion

  // #region Handlers filtros/paginação
  function update(patch) {
    const next = { ...local, ...patch };
    setLocal(next);
    onFiltersChange(next);
  }
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  function jumpToPage(p) { onPageChange(Math.min(Math.max(1, p), totalPages)); }
  // #endregion

  // #region Render helpers
  const renderBody = () => {
    if (loading) {
      return Array.from({ length: 6 }).map((_, i) => (
        <tr key={`sk-${i}`} className="skeleton-row" aria-hidden="true">
          <td colSpan="8">
            <div className="sk-line w40" />
            <div className="sk-line w25" />
          </td>
        </tr>
      ));
    }
    if (data.length === 0) {
      return (
        <>
          <tr>
            <td colSpan="8" className="empty-cell">Nenhum funcionário encontrado.</td>
          </tr>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={`empty-${i}`} className="row-filler" aria-hidden="true">
              <td colSpan="8" />
            </tr>
          ))}
        </>
      );
    }
    return (
      <>
        {data.map(f => (
          <tr key={f.id}>
            <td>
              <div className="cell-main">
                <span className="cell-title">{f.nome}</span>
                {f.cargo && <span className="cell-sub">{f.cargo}</span>}
              </div>
            </td>
            <td>{formatCPF(f.cpf)}</td>
            <td>{formatPhone(f.telefone)}</td>
            <td>{f.cargo}</td>
            <td>{formatDateISO(f.data_admissao)}</td>
            <td>{(f.perc_comissao_padrao || 0).toFixed(1)}%</td>
            <td>
              <span className={`tag ${f.ativo ? 'tag-active' : 'tag-inactive'}`}>
                {f.ativo ? 'Ativo' : 'Inativo'}
              </span>
            </td>
            <td className="actions">
              <button
                type="button"
                className="icon-btn"
                onClick={() => onEdit(f.id)}
                aria-label={`Editar ${f.nome}`}
              >
                <img src={iconEdit} alt="" />
              </button>
              <button
                type="button"
                className="icon-btn danger"
                onClick={() => { if (window.confirm('Remover funcionário?')) onDelete(f.id); }}
                aria-label={`Excluir ${f.nome}`}
              >
                <img src={iconTrash} alt="" />
              </button>
            </td>
          </tr>
        ))}
        {data.length < 6 &&
          Array.from({ length: 6 - data.length }).map((_, i) => (
            <tr key={`filler-${i}`} className="row-filler" aria-hidden="true">
              <td colSpan="8" />
            </tr>
          ))}
      </>
    );
  };
  // #endregion

  // #region Render
  return (
    <div className="funcionarios-module">
      <div className="funcionarios-head">
        <h2 className="funcionarios-title">Funcionários</h2>
        <button type="button" onClick={onCreate} className="btn btn-primary">
          <span className="btn-icon">+</span> Novo Funcionário
        </button>
      </div>
      <div className="table-wrapper" role="region" aria-label="Lista de funcionários">
        <div className="table-filters" role="form" aria-label="Filtros">
          <input
            placeholder="Buscar por nome, CPF, telefone ou cargo..."
            value={local.q}
            onChange={e => update({ q: e.target.value })}
            aria-label="Buscar"
            disabled={loading}
          />
          <select
            value={local.status}
            onChange={e => update({ status: e.target.value })}
            aria-label="Status"
            disabled={loading}
          >
            <option value="TODOS">Todos</option>
            <option value="ATIVOS">Ativos</option>
            <option value="INATIVOS">Inativos</option>
          </select>
          <select
            value={local.cargo}
            onChange={e => update({ cargo: e.target.value })}
            aria-label="Cargo"
            disabled={loading}
          >
            <option value="TODOS">Todos os cargos</option>
            {cargos.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="table-container">
          <table className="ct-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>CPF</th>
                <th>Telefone</th>
                <th>Cargo</th>
                <th>Admissão</th>
                <th>Comissão</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>{renderBody()}</tbody>
          </table>
        </div>
        <div className="ct-footer" role="navigation" aria-label="Paginação">
          <div className="result-count">Total: {total}</div>
          <div className="pagination">
            <button onClick={() => jumpToPage(1)} disabled={page === 1 || loading} aria-label="Primeira página">&laquo;</button>
            <button onClick={() => jumpToPage(page - 1)} disabled={page === 1 || loading} aria-label="Página anterior">&lsaquo;</button>
            <label className="page-jump">
              <span className="sr-only">Página atual</span>
              <input
                value={String(page)}
                onChange={e => { const v = e.target.value.replace(/\D/g, ''); if (v) jumpToPage(Number(v)); }}
                aria-label="Ir para página"
                inputMode="numeric"
                disabled={loading}
              />
              <span className="total-pages">/ {totalPages}</span>
            </label>
            <button onClick={() => jumpToPage(page + 1)} disabled={page === totalPages || loading} aria-label="Próxima página">&rsaquo;</button>
            <button onClick={() => jumpToPage(totalPages)} disabled={page === totalPages || loading} aria-label="Última página">&raquo;</button>
          </div>
        </div>
      </div>
    </div>
  );
  // #endregion
}
